<template>
  <iColumn width="fill" gap="none" data-test="event-room-outer">
    <iColumn
      gap="standard"
      width="fill"
      data-test="event-room-surface"
      :style-overrides="roomFrameStyles"
    >
      <iRow
        gap="small"
        align="left"
        vertical-align="middle"
        width="fill"
        wrap="nowrap"
      >
        <iBadge
          v-if="chrome.showStatusChip"
          data-test="room-preview-status"
          :label="stateLabel"
          :variant="badgeVariant"
          :pulse="previewState === 'live'"
          size="small"
        />
        <iText font-size="small">
          {{ headline }}
        </iText>
      </iRow>

      <iColumn
        v-if="previewState === 'pre-event' || previewState === 'early-access'"
        gap="standard"
        width="fill"
        wrap="nowrap"
        data-test="room-preview-hero"
        :style-overrides="{paddingTop: 'standard', paddingBottom: 'large'}"
      >
        <iColumn
          width="fill"
          :height="120"
          :style-overrides="{backgroundColor: 'dimmed', borderRadius: 'large'}"
        />
        <iColumn gap="extraSmall" width="fill">
          <iText
            font-size="verySmall"
            font-weight="dark"
            variant="subtle"
            text-case="uppercase"
          >
            Meeting · Sat, Sep 12 · 3:00 PM
          </iText>
          <iText font-size="extraLarge" font-weight="dark" variant="standard">
            Launch party
          </iText>
          <iText font-size="small" variant="standard">
            {{ previewState === 'pre-event' ? 'People can join any time before it starts' : 'People are arriving before the start' }}
          </iText>
        </iColumn>
        <iRow
          v-if="previewState === 'pre-event'"
          gap="standard"
          align="left"
          width="hug"
          wrap="nowrap"
        >
          <iColumn
            v-for="part in countdownParts"
            :key="part.label"
            width="hug"
            align="center"
            gap="none"
            :min-width="COUNTDOWN_BOX"
            :style-overrides="countdownBoxStyles"
          >
            <iText
              font-size="huge"
              font-weight="dark"
              variant="standard"
            >
              {{ part.value }}
            </iText>
            <iText font-size="footnote" variant="standard">
              {{ part.label }}
            </iText>
          </iColumn>
        </iRow>
        <iText
          v-else
          font-size="extraExtraLarge"
          font-weight="dark"
          variant="standard"
        >
          {{ compactCountdown }}
        </iText>
        <iRow
          v-if="chrome.showViewerCount"
          gap="small"
          vertical-align="middle"
          wrap="nowrap"
          data-test="room-preview-presence"
        >
          <iAvatarStack :people="previewPeople" size="small" />
          <iText font-size="small" variant="standard">
            3 people are already here.
          </iText>
        </iRow>
        <iButton variant="primary" size="large">
          Join now
        </iButton>
      </iColumn>

      <iColumn
        v-else-if="previewState === 'live'"
        gap="small"
        width="fill"
      >
        <iRow
          v-if="showsLiveChrome"
          gap="small"
          align="between"
          vertical-align="middle"
          width="fill"
          wrap="nowrap"
        >
          <iRow
            gap="small"
            vertical-align="middle"
            width="hug"
            wrap="nowrap"
          >
            <iText
              v-if="chrome.showEventTitle"
              font-size="small"
              font-weight="dark"
              variant="standard"
              data-test="room-preview-title"
            >
              Launch party
            </iText>
            <iText
              v-if="chrome.showElapsedTimer"
              font-size="small"
              variant="subtle"
              data-test="room-preview-timer"
            >
              12:34
            </iText>
          </iRow>
          <iRow
            gap="small"
            vertical-align="middle"
            width="hug"
            wrap="nowrap"
          >
            <iBadge
              v-if="chrome.showLiveBadge"
              label="LIVE"
              variant="striking"
              pulse
              size="small"
              data-test="room-preview-live-badge"
            />
            <iText
              v-if="chrome.showViewerCount"
              font-size="small"
              variant="subtle"
              data-test="room-preview-count"
            >
              3 joined
            </iText>
          </iRow>
        </iRow>
        <iColumn
          width="fill"
          align="center"
          vertical-align="middle"
          :min-height="STAGE_HEIGHT"
          :style-overrides="stageStyles"
        >
          <iText font-size="small" variant="standard">
            Active speaker
          </iText>
        </iColumn>

        <iRow
          gap="small"
          width="fill"
          wrap="nowrap"
        >
          <iColumn
            v-for="tile in tileRange"
            :key="tile"
            width="fill"
            align="center"
            vertical-align="middle"
            :min-height="TILE_HEIGHT"
            :style-overrides="tileStyles"
          >
            <iText font-size="footnote" variant="standard">
              Guest
            </iText>
          </iColumn>
        </iRow>

        <iRow
          gap="small"
          align="center"
          vertical-align="middle"
          width="fill"
          wrap="nowrap"
        >
          <iColumn
            v-for="control in CONTROLS"
            :key="control"
            width="hug"
            :min-height="CONTROL_SIZE"
            :min-width="CONTROL_SIZE"
            :style-overrides="controlStyles"
          />
        </iRow>
      </iColumn>

      <iColumn
        v-else
        gap="small"
        align="center"
        width="fill"
        :style-overrides="{paddingTop: 'large', paddingBottom: 'large'}"
      >
        <iText
          font-size="large"
          font-weight="dark"
          variant="standard"
        >
          Thanks for coming
        </iText>
        <iText font-size="small" variant="standard">
          A recap appears here once it is published
        </iText>
      </iColumn>
    </iColumn>
  </iColumn>
</template>

<script>
import {
  ROOM_PREVIEW_EARLY_ACCESS,
  ROOM_PREVIEW_ENDED,
  ROOM_PREVIEW_LIVE,
  ROOM_PREVIEW_STATE_LABELS,
  exampleTimingForRoomPreviewState,
  normalizeRoomPreviewState,
} from "%/dynamic-component-editor/helpers/event-room-preview-states";

const MOCK_TILE_COUNT = 4;
const CONTROLS = [0, 1, 2, 3];
const CONTROL_SIZE = 40;
const STAGE_HEIGHT = 220;
const TILE_HEIGHT = 84;
const COUNTDOWN_BOX = 84;

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;

// Pure mock — the builder must cost zero video bytes (no SDK, no token, no
// camera prompt). No iIcon either: it resolves icons over the network.
export default {
  name: "EventRoomPlaceholder",
  // Without this, the parent's fallthrough style-overrides land on the root
  // node and replace roomFrameStyles wholesale (last write wins).
  inheritAttrs: false,
  props: {
    previewState: {
      type: String,
      required: true,
    },
    chrome: {
      type: Object,
      required: false,
      default: () => ({
        showEventTitle: true,
        showElapsedTimer: true,
        showLiveBadge: true,
        showViewerCount: true,
        showStatusChip: true,
      }),
    },
  },
  data() {
    return {
      CONTROLS,
      CONTROL_SIZE,
      STAGE_HEIGHT,
      TILE_HEIGHT,
      COUNTDOWN_BOX,
      mountedAtSeconds: Math.floor(Date.now() / 1000),
    };
  },
  computed: {
    previewPeople() {
      return ["Ana", "Ben", "Cara"].map(name => ({ id: name, name }));
    },
    roomFrameStyles() {
      return {
        backgroundColor: "paper",
        borderRadius: "large",
        // Per-side keys only: iColumn's styleGuide has no combined `padding`,
        // and an unknown key silently falls back to the default.
        paddingTop: "standard",
        paddingBottom: "standard",
        paddingLeft: "standard",
        paddingRight: "standard",
      };
    },
    stageStyles() {
      return {
        backgroundColor: "subtle",
        borderRadius: "standard",
        paddingTop: "standard",
        paddingBottom: "standard",
        paddingLeft: "standard",
        paddingRight: "standard",
      };
    },
    tileStyles() {
      return {
        backgroundColor: "subtle",
        borderRadius: "standard",
      };
    },
    controlStyles() {
      return {
        backgroundColor: "subtle",
        borderRadius: "huge",
      };
    },
    countdownBoxStyles() {
      return {
        backgroundColor: "subtle",
        borderRadius: "standard",
        paddingTop: "standard",
        paddingBottom: "standard",
      };
    },
    showsLiveChrome() {
      return this.chrome.showEventTitle || this.chrome.showElapsedTimer || this.chrome.showLiveBadge || this.chrome.showViewerCount;
    },
    badgeVariant() {
      if (this.previewState === ROOM_PREVIEW_LIVE) {
        return "striking";
      }

      return this.previewState === ROOM_PREVIEW_ENDED ? "standard" : "branded";
    },
    tileRange() {
      return Array.from({ length: MOCK_TILE_COUNT }, (unused, index) => index);
    },
    // Fixed at mount, not a live clock: re-deriving timings every tick would
    // restart the countdown on every repaint.
    exampleTiming() {
      return exampleTimingForRoomPreviewState(this.previewState, this.mountedAtSeconds);
    },
    secondsRemaining() {
      return Math.max(0, this.exampleTiming.startTime - this.mountedAtSeconds);
    },
    countdownParts() {
      const remaining = this.secondsRemaining;
      const pad = value => String(value).padStart(2, "0");

      return [
        { label: "days", value: pad(Math.floor(remaining / SECONDS_PER_DAY)) },
        { label: "hours", value: pad(Math.floor(remaining % SECONDS_PER_DAY / SECONDS_PER_HOUR)) },
        { label: "mins", value: pad(Math.floor(remaining % SECONDS_PER_HOUR / SECONDS_PER_MINUTE)) },
      ];
    },
    compactCountdown() {
      const remaining = this.secondsRemaining;
      const minutes = Math.floor(remaining % SECONDS_PER_HOUR / SECONDS_PER_MINUTE);
      const seconds = remaining % SECONDS_PER_MINUTE;
      const pad = value => String(value).padStart(2, "0");

      return `${pad(minutes)}:${pad(seconds)}`;
    },
    stateLabel() {
      return ROOM_PREVIEW_STATE_LABELS[normalizeRoomPreviewState(this.previewState)];
    },
    headline() {
      switch (normalizeRoomPreviewState(this.previewState)) {
        case ROOM_PREVIEW_LIVE:
          return "The meeting is under way";
        case ROOM_PREVIEW_EARLY_ACCESS:
          return "Starting soon";
        case ROOM_PREVIEW_ENDED:
          return "This meeting has ended";
        default:
          return "Before the meeting starts";
      }
    },
  },
};
</script>
